import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { Coins, QrCode, SquareArrowOutUpRight } from 'lucide-react'
import { formatTokenAmount } from '@/lib/format'
import { Modal } from './modal'
import { AddressField } from './address-field'
import { SOL_MINT, USDC_MINT, USDT_MINT, tokenLogo } from '@/lib/default-tokens'
import { AssetChip } from './asset-chips'
import { Spinner } from './spinner'
import { Segmented } from './segmented'
import { FlowProgress } from './flow-progress'
import { errorMessage, useTRPC } from '@/lib/trpc'
import type { MeWallet, PrivateDepositView, VaultDepositView } from '@/lib/api-types'
import { useCustody, usePoolMode, CUSTODY_LABELS, type ExchangeId } from '@/lib/pool'
import { useSetCustody } from '@/lib/custody'
import { PlatformGroup, usePlatformOptions } from './platform-chip'
import { HEADER_CHIP, HeaderRule } from './header-chips'
import { isExchangeId } from '@/lib/pool'
import { countdownText, useCountdown } from '@/lib/eta'
import { markPortfolioFresh, useInvalidatePortfolio } from '@/lib/portfolio'

const STEPS = ['Funding', 'Routing', 'Depositing', 'Complete'] as const
const TOKEN_STEPS = ['Funding', 'Routing', 'Depositing', 'Complete'] as const
const TERMINAL = new Set(['completed', 'failed', 'stalled'])

function stepIndex(status: PrivateDepositView['status']) {
  switch (status) {
    case 'awaitingFunds':
      return 0
    case 'funded':
    case 'sweepSent':
      return 1
    case 'relayPending':
    case 'stalled':
      return 2
    case 'completed':
      return 3
    default:
      return -1
  }
}

/** raw units of the deposit's asset, formatted with its symbol */
function amountOf(raw: string | null | undefined, asset: PrivateDepositView['asset']) {
  if (!raw) return ''
  return `${formatTokenAmount(Number(raw) / 10 ** asset.decimals, asset.symbol, 6)} ${asset.symbol}`
}
/** the number alone, for lines that show the token as a chip */
function numberOf(raw: string | null | undefined, asset: PrivateDepositView['asset']) {
  if (!raw) return ''
  return formatTokenAmount(Number(raw) / 10 ** asset.decimals, asset.symbol, 6)
}

/**
 * One address for everything on Solana, no toggles (John, Oct 29 2026):
 * SOL, USDC and USDT arrive privately through Relay; any other token sent
 * to the same address is handed to the vault.
 */
export function DepositModal({ open, onClose, wallet, initialId = null }: { open: boolean; onClose: () => void; wallet: MeWallet; initialId?: string | null }) {
  const [footer, setFooter] = useState<React.ReactNode>(undefined)
  // the QR code is a square button in the header after the title, there while an address is waiting (John, Oct 31 2026)
  const [qrAddress, setQrAddress] = useState<string | null>(null)
  const [showQr, setShowQr] = useState(false)
  // a new address (or none on screen) folds the QR away again
  const setAddress = useCallback((address: string | null) => {
    setQrAddress(address)
    setShowQr(false)
  }, [])
  const qr: QrControl = { show: showQr, setAddress }
  // the platforms, after the QR code: the pool deposit puts its group here while an address waits (John, Nov 2 2026: "after the QR code, put like an angle bar and then do the three exchanges")
  const [picker, setPicker] = useState<React.ReactNode>(undefined)
  const poolMode = usePoolMode()
  return (
    <Modal
      open={open}
      onClose={onClose}
      // one size wider than the other dialogs: a full Solana address then fits on one line at the normal size (John, Oct 31 2026)
      width="lg"
      footer={footer}
      title={
        <span className="flex items-center gap-3">
          Deposit
          {qrAddress ? (
            <button
              type="button"
              className={HEADER_CHIP}
              aria-pressed={showQr}
              aria-label={showQr ? 'Hide QR code' : 'Show QR code'}
              title={showQr ? 'Hide QR code' : 'Show QR code'}
              onClick={() => setShowQr((v) => !v)}
            >
              <QrCode className="size-5" />
            </button>
          ) : null}
          {picker ? (
            <>
              <HeaderRule />
              {picker}
            </>
          ) : null}
        </span>
      }
    >
      {poolMode ? (
        <PoolDeposit wallet={wallet} onClose={onClose} setFooter={setFooter} setPicker={setPicker} initialId={initialId} qr={qr} />
      ) : (
        <PrivateDeposit wallet={wallet} onClose={onClose} setFooter={setFooter} qr={qr} />
      )}
    </Modal>
  )
}

/** the header's QR button: whether it is open, and how an address card tells the header what to encode */
type QrControl = { show: boolean; setAddress: (address: string | null) => void }

const POOL_STEPS = ['Funding', 'Depositing', 'Complete'] as const
type DepositAsset = 'SOL' | 'USDC' | 'USDT' | 'token'
const POOL_TERMINAL = new Set(['credited', 'failed', 'cancelled'])

function poolStep(status: VaultDepositView['status']) {
  switch (status) {
    case 'awaitingFunds':
      return 0
    case 'processing':
      return 1
    case 'credited':
      return 2
    default:
      return -1
  }
}

/** True once `active` has held for `ms`: a loader that would only flash stays hidden. */
function useHeld(active: boolean, ms: number) {
  const [held, setHeld] = useState(false)
  useEffect(() => {
    if (!active) return
    const timer = window.setTimeout(() => setHeld(true), ms)
    return () => {
      window.clearTimeout(timer)
      setHeld(false)
    }
  }, [active, ms])
  return held
}

/** Pool custody: one address takes SOL or any token; it lands in your balance a few seconds after Sonar sees it. */
function PoolDeposit({ wallet, onClose, setFooter, setPicker, initialId = null, qr }: { wallet: MeWallet; onClose: () => void; setFooter: (footer: React.ReactNode) => void; /** the header's platform group, there while the address waits */ setPicker: (picker: React.ReactNode) => void; initialId?: string | null; qr: QrControl }) {
  const trpc = useTRPC()
  const { exchange, dispenceVault, custody } = useCustody()
  const custodyLabel = CUSTODY_LABELS[custody]
  // where the deposit lands (John, Nov 1 2026): the chip picks the platform and the account remembers it
  const platformOptions = usePlatformOptions()
  const { set: setCustody } = useSetCustody()
  const queryClient = useQueryClient()
  const invalidatePortfolio = useInvalidatePortfolio()
  // opened on a deposit in progress (the activity bar's View): that one, not the waiting address
  const [activeId, setActiveId] = useState<string | null>(initialId)
  const [error, setError] = useState<string | null>(null)
  // an exchange: one vault address takes anything and routes SOL, USDC and USDT on to the exchange (John, Oct 31 2026);
  // without a vault, the exchange's own per-asset addresses. The link between the two is gone (John, Nov 1 2026: redundant).
  const direct = !dispenceVault
  const [asset, setAsset] = useState<DepositAsset>(dispenceVault ? 'token' : 'SOL')
  const windowInput = { walletId: wallet.id, asset, platform: custody }
  // every other platform's waiting address is read ahead, so a switch shows it at once (John, Nov 1 2026: "pre-warmed")
  useQueries({
    queries: platformOptions.flatMap((option) =>
      isExchangeId(option.id) && option.id !== custody
        ? [{ ...trpc.vault.openPoolDeposit.queryOptions({ walletId: wallet.id, asset: dispenceVault ? ('token' as const) : ('SOL' as const), platform: option.id }), staleTime: 30_000 }]
        : [],
    ),
  })
  // the address still waiting from last time; the dashboard reads it ahead so the dialog opens with it on screen
  const openKey = trpc.vault.openPoolDeposit.queryKey(windowInput)
  const existing = useQuery({ ...trpc.vault.openPoolDeposit.queryOptions(windowInput), staleTime: 30_000 })
  /** shows a deposit at once instead of waiting for its own query to come back */
  function show(deposit: VaultDepositView) {
    queryClient.setQueryData(trpc.vault.deposit.queryKey({ id: deposit.id }), deposit)
    queryClient.setQueryData(openKey, deposit)
    setActiveId(deposit.id)
  }
  const create = useMutation(
    trpc.vault.poolDepositAddress.mutationOptions({
      onSuccess: (deposit) => {
        show(deposit)
        void queryClient.invalidateQueries({ queryKey: trpc.vault.deposits.queryKey() })
      },
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  useEffect(() => {
    if (activeId !== null || create.isPending || create.isError) return
    if (existing.data && existing.data.status === 'awaitingFunds') show(existing.data)
    else if (existing.isSuccess) create.mutate(windowInput)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, existing.data, existing.isSuccess, asset])
  const active = useQuery({
    ...trpc.vault.deposit.queryOptions({ id: activeId ?? '' }),
    enabled: activeId !== null,
    refetchInterval: (query) => (query.state.data && POOL_TERMINAL.has(query.state.data.status) ? false : 2_000),
  })
  const deposit = active.data
  // switching tabs keeps the last card on screen, dimmed, until the next address is in: nothing blanks or jumps (John, Oct 31 2026)
  const [switching, setSwitching] = useState(false)
  const lastShown = useRef<VaultDepositView | undefined>(undefined)
  if (deposit) lastShown.current = deposit
  const shown = deposit ?? (switching ? lastShown.current : undefined)
  const stale = !deposit && shown !== undefined
  // the place, the asset tabs and the "send to this address" line only while the address waits for money (John, Nov 1 2026:
  // "why is it letting me click dropdown" on a finished deposit)
  const awaiting = !shown || shown.status === 'awaitingFunds'
  useEffect(() => {
    if (deposit) setSwitching(false)
  }, [deposit])
  useEffect(() => {
    if (!deposit) return
    void queryClient.invalidateQueries({ queryKey: trpc.vault.deposits.queryKey() })
    if (deposit.status === 'credited') void invalidatePortfolio()
    // a finished deposit is no longer the one to reopen with
    if (POOL_TERMINAL.has(deposit.status)) queryClient.setQueryData(openKey, null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit?.status])
  const waiting = useHeld(!deposit, 300)
  const over = !!deposit && POOL_TERMINAL.has(deposit.status)
  useEffect(() => {
    setFooter(
      over ? (
        <div className="flex gap-3">
          <button type="button" className="btn btn-secondary flex-1" disabled={create.isPending} onClick={() => { setActiveId(null); create.mutate({ walletId: wallet.id }) }}>
            New deposit
          </button>
          <button type="button" className="btn btn-primary flex-1" onClick={onClose}>Done</button>
        </div>
      ) : undefined,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, create.isPending])
  const symbol = (deposit?.tokenSymbol ?? 'SOL').toUpperCase()
  // the number alone: the chip beside it carries the ticker and the logo
  const amount = deposit?.sonarAmount && deposit.tokenDecimals !== null ? formatTokenAmount(Number(deposit.sonarAmount) / 10 ** deposit.tokenDecimals, symbol, 6) : null
  const finished = deposit?.status === 'credited'
  // the exchange's usual crediting time counting down from when it listed the deposit (John, Nov 1 2026)
  const countdown = useCountdown(deposit?.seenAt, deposit?.etaSeconds, deposit?.status === 'processing')
  // the place the record says the money is going, not the dialog's current pick: a window opened under one exchange can
  // forward to another, and the bar's chip reads the record (John, Nov 1 2026: the chip said Coinbase for a MEXC deposit)
  const placeLabel = deposit && isExchangeId(deposit.platform) ? CUSTODY_LABELS[deposit.platform] : custodyLabel
  const left = countdownText(countdown)
  const chip = deposit && deposit.status !== 'awaitingFunds' ? <AssetChip symbol={symbol} imageUrl={tokenLogo(deposit.tokenAddress, deposit.tokenImageUrl ?? undefined)} /> : null
  // nothing is said while the address only waits: a loader before anything was sent reads as progress that is not there (John, Oct 31 2026)
  const status = !deposit || deposit.status === 'awaitingFunds' ? null : deposit.status === 'processing' ? (
      <span className="flex flex-wrap items-center justify-center gap-1.5">
        {amount ?? 'Received'} {chip}{' '}
        {deposit.sonarStatus === 'sweeping'
          ? 'arrived, moving into your balance…'
          : deposit.sonarStatus === 'arrived'
            ? 'arrived, getting ready to move it…'
          : deposit.sonarStatus === 'forwarding'
            ? `arrived, moving to ${placeLabel}…`
            : exchange
              ? `on its way, ${placeLabel} is confirming it…${left ? ` ${left}` : ''}`
              : 'on its way…'}
      </span>
    ) : deposit.status === 'credited' ? (
      <span className="flex flex-wrap items-center justify-center gap-1.5">
        Done. {amount ?? ''} {chip} arrived.{' '}
        {deposit.transactionHash ? (
          <a className="text-accent" href={`https://solscan.io/tx/${deposit.transactionHash}`} target="_blank" rel="noreferrer">
            View txn <SquareArrowOutUpRight className="inline size-3 align-[-1px]" />
          </a>
        ) : null}
      </span>
    ) : (
      <span className="text-negative">{deposit.error ?? 'This deposit did not go through.'}</span>
    )
  // the platform group in the header, only while the address waits for money, like the dropdown it replaces
  // (John, Nov 2 2026: "instead of deposit to with the drop down, just say deposit to Coinbase, e.g. if I picked the Coinbase chip")
  const pickPlatform = (id: string) => {
    setSwitching(true)
    setActiveId(null)
    setError(null)
    setCustody.mutate({ custody: id as ExchangeId })
  }
  const platformKey = platformOptions.map((option) => option.id).join()
  useEffect(() => {
    setPicker(awaiting && platformOptions.length > 1 ? <PlatformGroup value={custody} options={platformOptions} disabled={setCustody.isPending} onChange={pickPlatform} /> : undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaiting, platformKey, custody, setCustody.isPending])
  useEffect(() => () => setPicker(undefined), [setPicker])
  return (
    <div className="flex min-w-0 flex-col gap-4">
      {awaiting && platformOptions.length > 1 ? (
        <p className="text-sm">
          <span className="text-muted">Deposit to</span> <span className="font-medium">{custodyLabel}</span>
        </p>
      ) : null}
      {awaiting && exchange && direct ? (
        <Segmented
          value={asset}
          onChange={(next) => {
            if (next === asset) return
            setSwitching(true)
            setAsset(next)
            setActiveId(null)
            setError(null)
          }}
          options={[
            { value: 'SOL', label: <TabLabel logo={tokenLogo(SOL_MINT)}>SOL</TabLabel> },
            { value: 'USDC', label: <TabLabel logo={tokenLogo(USDC_MINT)}>USDC</TabLabel> },
            { value: 'USDT', label: <TabLabel logo={tokenLogo(USDT_MINT)}>USDT</TabLabel> },
          ]}
        />
      ) : null}
      {awaiting ? (
        <p className="text-sm">
          {exchange && asset !== 'token' ? `Send ${asset} to this ${custodyLabel} address.` : exchange ? 'Send SOL, USDC, USDT or any Solana token to this address.' : 'Send SOL or any Solana token to this address.'}
        </p>
      ) : null}
      {!shown ? (
        // the same space as the address card, so nothing jumps; the shimmer only starts when the wait is real
        <div className="flex flex-col items-center gap-4" aria-busy="true">
          <div className={`h-12 w-full ${waiting ? 'skeleton' : ''}`} />
          <div className={`h-10 w-36 ${waiting ? 'skeleton' : ''}`} />
        </div>
      ) : (
        <div className={`flex min-w-0 flex-col gap-4 transition-opacity ${stale ? 'pointer-events-none opacity-40' : ''}`} aria-busy={stale}>
          {shown.status === 'awaitingFunds' ? <AddressCard address={shown.depositAddress} qr={qr} /> : null}
          {!exchange && shown.status === 'awaitingFunds' ? <p className="text-[11px] text-muted">Minimum $1. Anything less waits at the address until more arrives.</p> : null}
          {exchange && direct && shown.status === 'awaitingFunds' ? <p className="text-[11px] text-muted">{asset} only at this address.</p> : null}
          <FlowProgress
            flowKey={shown.id}
            labels={POOL_STEPS}
            step={poolStep(shown.status)}
            done={shown.status === 'credited'}
            failed={shown.status === 'failed' || shown.status === 'cancelled'}
            idle={shown.status === 'awaitingFunds'}
            fees={shown.status === 'credited' ? shown.fees : undefined}
            status={stale ? null : status}
          />
        </div>
      )}
      {create.isPending ? <Spinner className="mx-auto" /> : null}
      {error ? <p className="text-sm text-negative">{error}</p> : null}
      {exchange && !direct && shown?.status === 'awaitingFunds' && !stale ? (
        <button
          type="button"
          className="self-start text-xs text-muted underline-offset-2 hover:underline"
          disabled={create.isPending}
          onClick={() => {
            // another address while this one still waits: several deposits at once (John, Oct 31 2026)
            setSwitching(true)
            setActiveId(null)
            setError(null)
            create.mutate({ ...windowInput, fresh: true })
          }}
        >
          Use a new address for another deposit
        </button>
      ) : null}
    </div>
  )
}

/** a tab's name behind its coin's logo; the token tab gets a plain coins mark */
function TabLabel({ logo, children }: { logo?: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center justify-center gap-1.5">
      {logo ? <img src={logo} alt="" className="size-4 shrink-0 rounded-full" /> : <Coins className="size-4 shrink-0" aria-hidden="true" />}
      {children}
    </span>
  )
}

function AddressCard({ address, qr }: { address: string; qr: QrControl }) {
  // The QR stays folded until asked for (John, Oct 29 2026): the address is what most people copy.
  // Its button sits in the header; the card tells the header which address to encode while it is on screen.
  const { setAddress } = qr
  useEffect(() => {
    setAddress(address)
    return () => setAddress(null)
  }, [address, setAddress])
  return (
    <div className="flex flex-col items-center gap-4">
      <AddressField address={address} size="lg" />
      {qr.show ? (
        <div className="rounded-card bg-white p-3">
          <QRCodeSVG value={address} size={168} />
        </div>
      ) : null}
    </div>
  )
}

function PrivateDeposit({ wallet, onClose, setFooter, qr }: { wallet: MeWallet; onClose: () => void; setFooter: (footer: React.ReactNode) => void; qr: QrControl }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const config = useQuery(trpc.privateDeposit.config.queryOptions())
  const list = useQuery({ ...trpc.privateDeposit.list.queryOptions(), refetchInterval: 10_000 })
  const [activeId, setActiveId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const create = useMutation(
    trpc.privateDeposit.create.mutationOptions({
      onSuccess: (deposit) => {
        setActiveId(deposit.id)
        void queryClient.invalidateQueries({ queryKey: trpc.privateDeposit.list.queryKey() })
      },
      onError: (err) => setError(errorMessage(err)),
    }),
  )
  // Opening the dialog shows what still needs attention for this wallet (in
  // flight, or overdue) before opening a fresh one-time address. A finished
  // deposit is not shown again (John, Oct 29 2026).
  useEffect(() => {
    if (activeId !== null || !list.isSuccess || create.isPending || create.isError) return
    const mine = list.data.filter((d) => d.walletAddress === wallet.solanaAddress)
    const inFlight = mine.find((d) => d.status === 'funded' || d.status === 'sweepSent' || d.status === 'relayPending')
    const needsALook = mine.find((d) => d.status === 'failed' || d.status === 'stalled')
    const waiting = mine.find((d) => d.status === 'awaitingFunds')
    const pick = inFlight ?? needsALook ?? waiting
    if (pick) setActiveId(pick.id)
    else create.mutate({ walletId: wallet.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, list.isSuccess])
  const active = useQuery({
    ...trpc.privateDeposit.get.queryOptions({ id: activeId ?? '' }),
    enabled: activeId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      // an overdue fill keeps being checked once a minute server-side
      return status === 'completed' || status === 'failed' ? false : status === 'stalled' ? 30_000 : 2_000
    },
  })
  const deposit = active.data
  useEffect(() => {
    if (!deposit) return
    // keep the history list in step with the stepper
    void queryClient.invalidateQueries({ queryKey: trpc.privateDeposit.list.queryKey() })
    if (deposit.status === 'completed') {
      markPortfolioFresh()
      void queryClient.invalidateQueries({ queryKey: trpc.portfolio.get.queryKey() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit?.status])
  // a token cycle finishes when Sonar credits the vault deposit
  const vaultDeposit = useQuery({
    ...trpc.vault.deposit.queryOptions({ id: deposit?.vaultDepositId ?? '' }),
    enabled: !!deposit?.vaultDepositId,
    refetchInterval: (query) => (query.state.data && ['credited', 'failed', 'cancelled'].includes(query.state.data.status) ? false : 5_000),
  })
  const credited = vaultDeposit.data?.status === 'credited'
  useEffect(() => {
    if (credited) {
      markPortfolioFresh()
      void queryClient.invalidateQueries({ queryKey: trpc.portfolio.get.queryKey() })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credited])
  const cfg = config.data
  const minSol = cfg ? Number(cfg.sol.minRaw) / 10 ** cfg.sol.decimals : undefined
  const minStable = cfg ? Number(cfg.stable.minRaw) / 10 ** cfg.stable.decimals : undefined
  const over = !!deposit && TERMINAL.has(deposit.status)
  useEffect(() => {
    setFooter(
      over ? (
        <div className="flex gap-3">
          <button type="button" className="btn btn-secondary flex-1" disabled={create.isPending} onClick={() => create.mutate({ walletId: wallet.id })}>
            New deposit
          </button>
          <button type="button" className="btn btn-primary flex-1" onClick={onClose}>Done</button>
        </div>
      ) : undefined,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [over, create.isPending])
  const tokenCycle = deposit?.asset.kind === 'token'
  // a token's "Complete" waits for Sonar's credit; its hand-over is step 2 while Sonar sweeps
  const step = !deposit ? -1 : tokenCycle && deposit.status === 'completed' ? (credited ? 3 : 2) : stepIndex(deposit.status)
  const steps = tokenCycle ? TOKEN_STEPS : STEPS
  const finished = deposit?.status === 'completed' && (!tokenCycle || credited)

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <p className="text-sm">Send SOL or any Solana token to this address.</p>

      {!deposit ? (
        <div className="flex flex-col items-center gap-4">
          <div className="skeleton h-12 w-full" />
          <div className="skeleton h-10 w-36" />
        </div>
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {deposit.status === 'awaitingFunds' ? <AddressCard address={deposit.depositAddress} qr={qr} /> : null}
          <FlowProgress
            flowKey={deposit?.id}
            labels={steps.map((label) => {
              const parts = deposit.sweeps.length
              const sent = deposit.sweeps.filter((c) => c.signature).length
              return label === 'Routing' && parts > 1 ? `Routing ${Math.min(sent, parts)}/${parts}` : label
            })}
            step={step}
            done={finished}
            failed={deposit.status === 'failed'}
            idle={deposit.status === 'awaitingFunds'}
            fees={finished ? deposit.fees : undefined}
            status={deposit.status === 'awaitingFunds' ? null : <DepositStatusLine deposit={deposit} credited={credited} />}
          />
          {deposit.status === 'awaitingFunds' ? (
            <p className="text-[11px] text-muted">
              Minimums {minSol ?? '…'} SOL, {minStable ?? '…'} USDC or USDT.
            </p>
          ) : null}
        </div>
      )}
      {create.isPending ? <Spinner className="mx-auto" /> : null}
      {error ? <p className="text-sm text-negative">{error}</p> : null}
    </div>
  )
}

function DepositStatusLine({ deposit, credited }: { deposit: PrivateDepositView; credited: boolean }) {
  const asset = deposit.asset
  const fmt = (raw: string | null | undefined) => amountOf(raw, asset)
  const parts = deposit.sweeps.length
  const sent = deposit.sweeps.filter((c) => c.signature)
  const paid = deposit.sweeps.filter((c) => c.completedAt).length
  const latest = sent[sent.length - 1]
  if (asset.kind === 'token' && deposit.status !== 'awaitingFunds') {
    const chip = <AssetChip symbol={asset.symbol.toUpperCase()} imageUrl={tokenLogo(asset.mint ?? undefined, asset.imageUrl ?? undefined)} />
    switch (deposit.status) {
      case 'funded':
      case 'sweepSent':
        return (
          <span className="flex flex-wrap items-center justify-center gap-1.5">
            {numberOf(deposit.fundedLamports, asset)} {chip} received, on its way…{' '}
            {deposit.forward?.signature ? (
              <a className="text-accent" href={`https://solscan.io/tx/${deposit.forward.signature}`} target="_blank" rel="noreferrer">
                View transfer
              </a>
            ) : null}
          </span>
        )
      case 'completed':
        return (
          <span className="flex flex-wrap items-center justify-center gap-1.5">
            Done. {numberOf(deposit.fundedLamports, asset)} {chip} {credited ? 'arrived.' : 'arrives shortly.'}{' '}
            {deposit.forward?.signature ? (
              <a className="text-accent" href={`https://solscan.io/tx/${deposit.forward.signature}`} target="_blank" rel="noreferrer">
                View transfer
              </a>
            ) : null}
          </span>
        )
      case 'failed':
        return (
          <span>
            Not through yet, retrying…
          </span>
        )
      default:
        return null
    }
  }
  switch (deposit.status) {
    case 'funded':
      return (
        <span>
          {fmt(deposit.fundedLamports)} received, on its way…
        </span>
      )
    case 'sweepSent':
      return (
        <span>
          Routing {fmt(deposit.fundedLamports)}{parts > 1 ? ` in ${parts} parts` : ''}…{' '}
          {latest?.signature ? (
            <a className="text-accent" href={`https://solscan.io/tx/${latest.signature}`} target="_blank" rel="noreferrer">
              View deposit <SquareArrowOutUpRight className="inline size-3 align-[-1px]" />
            </a>
          ) : null}
        </span>
      )
    case 'relayPending':
      return (
        <span>
          Arriving{parts > 1 ? `, ${paid} of ${parts} parts` : ''}…
        </span>
      )
    case 'completed':
      return (
        <span>
          Done. {fmt(deposit.fill?.receivedLamports ?? deposit.sweep?.expectedOutLamports ?? null)} arrived.{' '}
          {deposit.fill?.txHash ? (
            <a className="text-accent" href={`https://solscan.io/tx/${deposit.fill.txHash}`} target="_blank" rel="noreferrer">
              View txn <SquareArrowOutUpRight className="inline size-3 align-[-1px]" />
            </a>
          ) : null}
        </span>
      )
    case 'failed':
      return (
        <span>
          Not through yet, retrying…
        </span>
      )
    case 'stalled':
      return (
        <span className="text-warning">
          Taking longer than usual{parts > 1 ? `, ${paid} of ${parts} parts arrived` : ''}…
        </span>
      )
  }
}
